import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const MobileNavigation = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnimating, setIsAnimating] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const navigationItems = [
    { path: '/system', icon: '🏠', label: 'Dashboard' },
    { path: '/hack-the-planet', icon: '🌍', label: 'Hack The Planet' },
    { path: '/lifestyle', icon: '🛒', label: 'Life Style' },
    { path: '/chat', icon: '🐰', label: 'White Rabbit' },
    { path: '/top-hacker', icon: '🏆', label: 'Leader Board' },
    { path: '/profile', icon: '👤', label: 'Profile' },
  ];

  // Add admin link if user is admin
  if (user?.role === 'admin') {
    navigationItems.splice(-1, 0, { path: '/admin', icon: '👑', label: 'Admin Panel' });
  }

  if (!isAnimating && !isOpen) return null;

  const mobileNavContent = (
    <>
      {/* Overlay */}
      <div 
        className={`mobile-nav-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1999,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'all 0.3s ease',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
        }}
      />

      {/* Navigation Container */}
      <div 
        className={`mobile-nav-container ${isOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          width: '100%',
          background: 'linear-gradient(180deg, #002200 0%, #001100 50%, #000800 100%)',
          borderBottom: '2px solid #40ff40',
          transform: isOpen ? 'translateY(0)' : 'translateY(-100%)',
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'all 0.3s ease',
          zIndex: 2000,
          boxShadow: '0 8px 32px rgba(0, 255, 65, 0.2)',
          maxHeight: 'calc(100vh - 60px)',
          overflowY: 'auto',
        }}
      >
        {/* Navigation Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px',
          borderBottom: '1px solid rgba(64, 255, 64, 0.3)',
        }}>
          <h3 style={{
            color: '#40ff60',
            fontFamily: "'VT323', monospace",
            fontSize: '1.2rem',
            margin: 0,
            textShadow: '0 0 5px #00ff41',
            textTransform: 'uppercase',
            letterSpacing: '2px',
          }}>
            Navigation
          </h3>
          
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #00ff41',
              color: '#00ff41',
              fontSize: '1.5rem',
              padding: '8px',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s',
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#00ff41';
              e.target.style.color = '#000800';
              e.target.style.boxShadow = '0 0 8px #00ff41';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = '#00ff41';
              e.target.style.boxShadow = 'none';
            }}
          >
            ✕
          </button>
        </div>

        {/* Navigation Menu */}
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
        }}>
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.path;
            
            return (
              <li key={item.path} style={{
                borderBottom: '1px solid rgba(64, 255, 64, 0.2)',
              }}>
                <button
                  onClick={() => handleNavigation(item.path)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    color: isActive ? '#ffffff' : '#C0FFC0',
                    textDecoration: 'none',
                    fontFamily: "'VT323', monospace",
                    fontSize: '1.1rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    transition: 'all 0.3s',
                    border: 'none',
                    background: isActive ? 'rgba(64, 255, 64, 0.2)' : 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.target.style.background = 'rgba(64, 255, 64, 0.1)';
                      e.target.style.color = '#ffffff';
                      e.target.style.boxShadow = 'inset 0 0 15px rgba(64, 255, 64, 0.2)';
                      e.target.style.transform = 'translateX(5px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#C0FFC0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.transform = 'translateX(0)';
                    }
                  }}
                  onTouchStart={(e) => {
                    if (!isActive) {
                      e.target.style.background = 'rgba(64, 255, 64, 0.2)';
                      e.target.style.transform = 'translateX(2px)';
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (!isActive) {
                      setTimeout(() => {
                        e.target.style.background = 'transparent';
                        e.target.style.transform = 'translateX(0)';
                      }, 150);
                    }
                  }}
                >
                  <span style={{
                    fontSize: '1.3rem',
                    minWidth: '24px',
                    textAlign: 'center',
                  }}>
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              </li>
            );
          })}

          {/* Logout Button */}
          <li style={{
            borderTop: '1px solid rgba(255, 68, 68, 0.3)',
            marginTop: '12px',
          }}>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                color: '#ff4444',
                textDecoration: 'none',
                fontFamily: "'VT323', monospace",
                fontSize: '1.1rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                transition: 'all 0.3s',
                border: 'none',
                background: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                minHeight: '44px',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 68, 68, 0.1)';
                e.target.style.color = '#ff6666';
                e.target.style.boxShadow = 'inset 0 0 15px rgba(255, 68, 68, 0.2)';
                e.target.style.transform = 'translateX(5px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#ff4444';
                e.target.style.boxShadow = 'none';
                e.target.style.transform = 'translateX(0)';
              }}
              onTouchStart={(e) => {
                e.target.style.background = 'rgba(255, 68, 68, 0.2)';
                e.target.style.transform = 'translateX(2px)';
              }}
              onTouchEnd={(e) => {
                setTimeout(() => {
                  e.target.style.background = 'transparent';
                  e.target.style.transform = 'translateX(0)';
                }, 150);
              }}
            >
              <span style={{
                fontSize: '1.3rem',
                minWidth: '24px',
                textAlign: 'center',
              }}>
                🚪
              </span>
              Logout
            </button>
          </li>
        </ul>
      </div>
    </>
  );

  return createPortal(mobileNavContent, document.body);
};

export default MobileNavigation;